import { ArrowRight, Copy, Check, FilePlus2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const eur = (n) => `${(Number(n) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

/**
 * Jun-2026 (User-Wunsch) — wiederverwendbare Ergebnis-Karte für einen
 * KI-Buchungssatz-Vorschlag (genutzt im Buchhaltung-Tab UND im Beleg-Modal).
 * onAddToStapel (optional) blendet den „Zum Buchungsstapel hinzufügen"-Button ein.
 */
export default function KiBuchungssatzResult({ result, onAddToStapel }) {
    const [copied, setCopied] = useState(false);
    const [adding, setAdding] = useState(false);
    if (!result) return null;

    const copySatz = () => {
        const txt = `${result.kontenrahmen} | Soll ${result.soll_konto} (${result.soll_konto_name}) an Haben ${result.haben_konto} (${result.haben_konto_name}) | ${eur(result.betrag_brutto)} | BU ${result.steuerschluessel || "-"}`;
        navigator.clipboard?.writeText(txt);
        setCopied(true); setTimeout(() => setCopied(false), 1500);
        toast.success("Buchungssatz kopiert");
    };

    const addToStapel = async () => {
        if (!onAddToStapel) return;
        setAdding(true);
        try { await onAddToStapel(result); } finally { setAdding(false); }
    };

    return (
        <div className="bg-white border-2 border-[#FFCC00] rounded-sm p-5" data-testid="ki-bk-result">
            <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-bold uppercase tracking-wider bg-[#111827] text-[#FFCC00] px-2 py-0.5 rounded-sm">{result.kontenrahmen}</span>
                <div className="flex items-center gap-2">
                    {typeof result.konfidenz === "number" && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Konfidenz {Math.round(result.konfidenz * 100)}%</span>
                    )}
                    {onAddToStapel && (
                        <button onClick={addToStapel} disabled={adding}
                            className="inline-flex items-center gap-1 text-xs font-bold text-[#111827] bg-[#FFCC00] hover:bg-[#e6b800] px-2.5 py-1 rounded-sm disabled:opacity-60"
                            title="Diesen Buchungssatz in den DATEV-Buchungsstapel legen" data-testid="ki-bk-add-stapel">
                            <FilePlus2 className="w-3.5 h-3.5" /> {adding ? "…" : "Zum Buchungsstapel"}
                        </button>
                    )}
                    <button onClick={copySatz} className="text-gray-400 hover:text-[#111827]" title="Buchungssatz kopieren" data-testid="ki-bk-copy">
                        {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                    </button>
                </div>
            </div>

            <div className="flex items-stretch gap-2 mb-4">
                <div className="flex-1 bg-emerald-50 border border-emerald-200 rounded-sm p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Soll</div>
                    <div className="font-mono-num font-black text-xl text-[#111827]" data-testid="ki-bk-soll">{result.soll_konto}</div>
                    <div className="text-xs text-gray-600">{result.soll_konto_name}</div>
                </div>
                <div className="flex items-center text-gray-400"><ArrowRight className="w-5 h-5" /></div>
                <div className="flex-1 bg-sky-50 border border-sky-200 rounded-sm p-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-sky-700">Haben</div>
                    <div className="font-mono-num font-black text-xl text-[#111827]" data-testid="ki-bk-haben">{result.haben_konto}</div>
                    <div className="text-xs text-gray-600">{result.haben_konto_name}</div>
                </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-sm">
                <div><div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Brutto</div><div className="font-mono-num font-bold">{eur(result.betrag_brutto)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Netto</div><div className="font-mono-num">{eur(result.betrag_netto)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">USt {result.ust_satz}%</div><div className="font-mono-num">{eur(result.ust_betrag)}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">BU-Schlüssel</div><div className="font-bold">{result.steuerschluessel || "—"}{result.steuer_hinweis ? <span className="block text-[10px] text-gray-400 font-normal">{result.steuer_hinweis}</span> : null}</div></div>
            </div>

            {result.begruendung && (
                <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 text-sm text-gray-700 mb-3" data-testid="ki-bk-begruendung">
                    <span className="font-bold">Begründung: </span>{result.begruendung}
                </div>
            )}

            {Array.isArray(result.alternativen) && result.alternativen.length > 0 && (
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Alternativen</div>
                    <ul className="space-y-1">
                        {result.alternativen.map((a, i) => (
                            <li key={i} className="text-xs text-gray-600 flex items-start gap-1.5">
                                <span className="font-mono-num font-bold text-[#111827]">{a.soll_konto} → {a.haben_konto}</span>
                                <span className="text-gray-400">·</span>
                                <span>{a.soll_konto_name} an {a.haben_konto_name}{a.begruendung ? ` — ${a.begruendung}` : ""}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            <p className="text-[10px] text-gray-400 mt-4">KI-Vorschlag ({result.model}) — bitte vor Verbuchung fachlich prüfen.</p>
        </div>
    );
}
